<script setup lang="ts">
import {useApi} from '@/ts/composables/stateful/use-api';
import {
  QForm,
  useQuasar,
} from 'quasar';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import BalmPasswordInput from '@/vue/atoms/balm-password-input.vue';
import {ref, type ComponentPublicInstance, computed} from 'vue';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useI18n} from 'vue-i18n';
import {
  usePasswordRules,
  useRequiredRule,
} from '@/ts/composables/pure/use-input-rules';
import {useUserStore} from '@/ts/store/user-store';
import {analyticsService} from '@/ts/services/analytics-service';
import {shouldEnableAnalytics} from '@/ts/utils/analytics-consent';

const api = useApi();
const {t: $t} = useI18n();
const paswordRules = usePasswordRules();
const requiredRule = useRequiredRule();
const $q = useQuasar();
const userStore = useUserStore();

/**
 * Whether this account is entered with a password at all (IPD-2844). One created through Google
 * is not: its password stopped opening it the moment it was linked, so a form asking for the
 * current one asks about something that does not exist.
 *
 * Defaults to showing the form. The flag is optional while the backend that sends it is not
 * deployed everywhere, and a password account that cannot see the form is the worse of the two
 * ways to be wrong.
 */
const hasPassword = computed<boolean>(() => userStore.userData?.hasPassword !== false);

const currentPassword = ref<string>('');
const newPassword = ref<string>('');
const repeatPassword = ref<string>('');

const formKey = ref(0); // Key for forcing form re-render once the form is submitted

const [updateAnalyticsPreference, analyticsLoading] = useLoadingNotification(
  async(value: boolean) => {
    await api.me.putConsentApproval({
      consentType: 'product_improvement',
      approved: value,
    });

    if (userStore.userData?.consents?.productImprovement) {
      userStore.userData.consents.productImprovement.approved = value;
    } else {
      const updatedUser = await api.me.getMe();
      userStore.setUserData(updatedUser);
    }

    // Update analytics service — use updated store state to check both consents
    if (shouldEnableAnalytics(userStore.userData?.consents)) {
      analyticsService.optInCapturing();
    } else {
      analyticsService.optOutCapturing();
    }
  },
  () => {
    $q.notify($t('analytics_preferences_updated'));
  },
);

const analyticsEnabled = computed({
  get: () => userStore.userData?.consents?.productImprovement?.approved ?? false,
  set: (value: boolean) => {
    void updateAnalyticsPreference(value);
  },
});

const [updateResearchPreference, researchLoading] = useLoadingNotification(
  async(value: boolean) => {
    await api.me.putConsentApproval({
      consentType: 'research',
      approved: value,
    });

    if (userStore.userData?.consents?.research) {
      userStore.userData.consents.research.approved = value;
    } else {
      // Research consent was added after users already existed. Users created before this change
      // don't have a research consent record, so the BE returns null. After the PUT creates the
      // first record, we refetch to get the full consent object.
      const updatedUser = await api.me.getMe();
      userStore.setUserData(updatedUser);
    }
  },
  () => {
    $q.notify($t('research_preferences_updated'));
  },
);

const researchEnabled = computed({
  get: () => userStore.userData?.consents?.research?.approved ?? false,
  set: (value: boolean) => {
    void updateResearchPreference(value);
  },
});

const [updateAnalyticsAnonymizedPreference, analyticsAnonymizedLoading] = useLoadingNotification(
  async(value: boolean) => {
    await api.me.putConsentApproval({
      consentType: 'analytics_anonymized',
      approved: value,
    });

    if (userStore.userData?.consents?.analyticsAnonymized) {
      userStore.userData.consents.analyticsAnonymized.approved = value;
    } else {
      // First toggle for this user — refetch to get the full consent object from BE
      const updatedUser = await api.me.getMe();
      userStore.setUserData(updatedUser);
    }

    // Suppress or resume event emission immediately
    if (shouldEnableAnalytics(userStore.userData?.consents)) {
      analyticsService.optInCapturing();
    } else {
      analyticsService.optOutCapturing();
    }
  },
  () => {
    $q.notify($t('analytics_anonymized_preferences_updated'));
  },
);

const analyticsAnonymizedEnabled = computed({
  get: () => userStore.userData?.consents?.analyticsAnonymized?.approved ?? false,
  set: (value: boolean) => {
    void updateAnalyticsAnonymizedPreference(value);
  },
});

const [changePassword, loading] = useLoadingNotification(
  async() => api.me.changePassword({
    currentPassword: currentPassword.value,
    newPassword: newPassword.value,
  }),
  () => {
    $q.notify($t('password_changed'));
    // Clear values and force form re-render to reset validation states
    currentPassword.value = '';
    newPassword.value = '';
    repeatPassword.value = '';
    formKey.value++;
  },
);

function repeatPasswordRule(val: string): boolean | string {
  return val === newPassword.value || $t('paswords_dont_match');
}

function cantUseCurrentPassword(val: string): boolean | string {
  return val !== currentPassword.value || $t('cant_use_current_password');
}

function onValidationError(element: any): void {
  ((element as ComponentPublicInstance).$el as HTMLElement).scrollIntoView({behavior: 'smooth'});
}
</script>

<template>
  <div class="account-tab-page">
    <h3 class="info-title">
      {{ $t('security') }}
    </h3>
    
    <div class="security-content">
      <!-- Left Section: Password Change -->
      <div class="security-section left-section">
        <h3 class="change-password-title">
          {{ hasPassword ? $t('change_password') : $t('how_you_sign_in') }}
        </h3>

        <!-- No password to change: say where it lives instead of offering a form that cannot
             work. The link leaves for Google, which is the only place this one can be changed. -->
        <p v-if="!hasPassword" class="google-account-note">
          {{ $t('password_managed_by_google') }}
          <a
            class="google-account-link"
            href="https://myaccount.google.com/security"
            target="_blank"
            rel="noopener noreferrer"
          >{{ $t('google_account_security') }}</a>
        </p>
        <q-form 
          v-if="hasPassword"
          :key="formKey"
          class="change-password-form" 
          @submit="changePassword"
          @validation-error="onValidationError"
        >
          <!--https://www.w3schools.com/tags/att_input_autocomplete.asp-->
          <balm-password-input
            v-model="currentPassword"
            :rules="[requiredRule]"
            :label="$t('current_password')"
            autocomplete="current-password"
          />
          <balm-password-input
            v-model="newPassword"
            :rules="[...paswordRules, cantUseCurrentPassword]"
            :label="$t('new_password')"
            autocomplete="new-password"
          />
          <balm-password-input
            v-model="repeatPassword"
            :rules="[repeatPasswordRule]"
            :label="$t('repeat_password')"
            autocomplete="new-password"
          />

          <div class="password-requirements-card">
            <h5 class="title">
              {{ $t('password_requirements') }}
            </h5>
            <ul class="requirements-list">
              <li>{{ $t('password_required_8') }}</li>
              <li>{{ $t('password_required_less_70') }}</li>
              <li>{{ $t('password_required_upper') }}</li>
              <li>{{ $t('password_required_lower') }}</li>
              <li>{{ $t('password_required_number') }}</li>
              <li>{{ $t('password_required_special') }}</li>
            </ul>
          </div>
        
          <balm-btn
            :label="$t('change_password')"
            type="submit"
            theme="green"
            class="change-passw-submt"
            paddings="p2-5"
            :loading="loading"
          />
        </q-form>
      </div>
      
      <!-- Right Section -->
      <div class="security-section right-section">
        <div class="consents-content">
          <div class="consent-card">
            <div class="consent-header">
              <h4 class="consent-title">
                {{ $t('product_improvement') }}
              </h4>
              <q-toggle
                v-model="analyticsEnabled"
                :aria-label="$t('product_improvement')"
                color="positive"
                size="md"
                :disable="analyticsLoading"
              />
            </div>
            <p class="consent-description">
              {{ $t('i_agree_to_product_improvement') }}
            </p>
          </div>
          <div class="consent-card">
            <div class="consent-header">
              <h4 class="consent-title">
                {{ $t('anonymization') }}
              </h4>
              <q-toggle
                v-model="researchEnabled"
                :aria-label="$t('anonymization')"
                color="positive"
                size="md"
                :disable="researchLoading"
              />
            </div>
            <p class="consent-description">
              {{ $t('i_agree_to_anonymization') }}
            </p>
          </div>
          <div class="consent-card">
            <div class="consent-header">
              <h4 class="consent-title">
                {{ $t('analytics_anonymized') }}
              </h4>
              <q-toggle
                v-model="analyticsAnonymizedEnabled"
                :aria-label="$t('analytics_anonymized')"
                color="positive"
                size="md"
                :disable="analyticsAnonymizedLoading"
              />
            </div>
            <p class="consent-description">
              {{ $t('i_agree_to_analytics_anonymized') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.account-tab-page {
  @include q-card-white;
  padding: var(--Spacing-spacing-5);
}

.info-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-5);
}

.security-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);
  margin-top: var(--Spacing-spacing-5);
  
  @include screen-md {
    flex-direction: row;
    gap: var(--Spacing-spacing-6);
  }
}

.security-section {
  flex: 1;
  
  &.left-section {
    @include screen-md {
      flex: 1;
    }
  }
  
  &.right-section {
    @include screen-md {
      flex: 1;
    }
  }
}

.change-password-title {
  @include font-h3;
    margin-bottom: var(--Spacing-spacing-5);
}

.google-account-note {
  @include font-body;

  margin: 0;
  color: var(--Neutral-UI-neutral-ui-80);
}

// The app's prose link, as the legal pages draw it: underlined at rest and not only coloured.
// On this face it is the one way out of the section, so a reader who cannot separate the blue
// from the paragraph around it is told where their password lives and not how to reach it.
.google-account-link {
  color: var(--Functional-wl-link-active);
  text-decoration: underline;

  &:visited {
    color: var(--Functional-wl-active-link);
  }

  &:hover,
  &:focus-visible {
    text-decoration: none;
  }
}

.change-password-form {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.password-requirements-card {
  @include q-card-20;
  display: flex;
  flex-direction: column;
  padding: var(--Spacing-spacing-4);
  margin: var(--Spacing-spacing-4) 0;
  gap: var(--Spacing-spacing-3);
  max-width: fit-content;

  .title {
    @include font-body-sm-bold;
  }

  .requirements-list {
    margin: 0;
    padding: 0 var(--Spacing-spacing-4);
    list-style-type: disc;
    
    li {
      @include font-body-sm;
      color: var(--Neutral-UI-neutral-ui-100);
      margin-bottom: var(--Spacing-spacing-2);
      line-height: 1.4;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.change-passw-submt {
  margin-top: var(--Spacing-spacing-2);
  align-self: flex-start;
}

.consents-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);

  .consent-card {
    @include q-card-10;
    padding: var(--Spacing-spacing-4);

    .consent-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--Spacing-spacing-3);
    }

    .consent-title {
      @include font-body-bold;
      margin: 0;
    }

    .consent-description {
      @include font-body-sm;
      color: var(--Neutral-UI-neutral-ui-100);
      margin: 0;
      line-height: 1.5;
    }
  }
}

</style>


